import { css } from "@linaria/core";
import React from "react";
import { spacings } from "@ui/Theme";
import { useDisplayFilters } from "../../../../api/useDisplayFilters";
import t from "../../../../i18n";
import FilterSelect from "@ui/Filter/FilterSelect";

import capitalize from "../../../../lib/capitalize";
import Button from "@ui/Button";
import { groupBy } from "diagnostics-lib/iterable";
import { useDisplayFilterPreferences } from "../useDisplayFilterPreferences";

export function DisplayFiltersSelect({ projectId }: { projectId: string }) {
  const displayFilters = useDisplayFilters(projectId);
  const {
    selectedFilterIds,
    defaultFilterIds,
    selectedGroupedFilterIds,
    selectedUngroupedFilterIds,
    setDisplayFilterPreferences,
  } = useDisplayFilterPreferences(projectId);

  const filtersByGroup = groupBy(displayFilters, f => f.group?.trim() ?? "__ungrouped__");
  const groupNames = Array.from(filtersByGroup.keys()).filter(g => g !== "__ungrouped__");
  const ungroupedFilterIds = new Set(
    (filtersByGroup.get("__ungrouped__") ?? []).map(displayFilter => displayFilter.id),
  );

  const toggleMultiSelectDisplayFilter = (chosenFilterIds: string[]) => {
    setDisplayFilterPreferences(currentPreferences => {
      const currentSelectedIds = Array.from(currentPreferences ?? defaultFilterIds);
      const selectedOutsideUngrouped = currentSelectedIds.filter(id => !ungroupedFilterIds.has(id));

      return [...selectedOutsideUngrouped, ...chosenFilterIds];
    });
  };

  const toggleSingleSelectDisplayFilterGroup = (
    chosenGroupFilterIds: string[],
    availableFilterIds: string[],
  ) => {
    setDisplayFilterPreferences(_ => {
      if (chosenGroupFilterIds.length === 0) {
        return [...selectedFilterIds.filter(id => !availableFilterIds.includes(id))];
      }

      const otherPrefs = selectedFilterIds.filter(
        id => selectedFilterIds.includes(id) && !chosenGroupFilterIds.includes(id),
      );

      return [...otherPrefs, ...chosenGroupFilterIds.filter(id => !selectedFilterIds.includes(id))];
    });
  };

  const resetDisplayFilterValues = () => {
    setDisplayFilterPreferences(undefined);
  };

  const firstUngroupedFilter = (filtersByGroup.get("__ungrouped__") ?? []).find(filter =>
    selectedUngroupedFilterIds.includes(filter.id),
  );

  const ungroupedSelectTitle = t("project.display_filter.select.ungrouped.title", {
    filterName: firstUngroupedFilter?.name,
    selectedCount: selectedUngroupedFilterIds.length,
    totalCount: ungroupedFilterIds.size ?? 0,
  });

  if (displayFilters.length === 0) return null;

  return (
    <div className={displayFilterSelectorsContainerCss}>
      {groupNames.map(group => {
        const filters = filtersByGroup.getOrThrow(group);

        return (
          <FilterSelect
            key={group}
            title={capitalize(group)}
            options={filters.map(displayFilter => ({
              label: displayFilter.name,
              value: displayFilter.id,
            }))}
            values={filters.filter(f => selectedGroupedFilterIds.includes(f.id)).map(f => f.id)}
            setValues={clickedValues => {
              toggleSingleSelectDisplayFilterGroup(
                Array.isArray(clickedValues) ? clickedValues : [clickedValues],
                filters.map(f => f.id),
              );
            }}
            width={260}
          />
        );
      })}
      {ungroupedFilterIds.size > 0 && (
        <FilterSelect
          title={
            groupNames.length === 0
              ? t("project.display_filter.select.ungrouped_vanilla.title")
              : ungroupedSelectTitle
          }
          options={filtersByGroup.getOrThrow("__ungrouped__").map(displayFilter => ({
            label: displayFilter.name,
            value: displayFilter.id,
          }))}
          values={filtersByGroup
            .getOrThrow("__ungrouped__")
            .filter(f => selectedUngroupedFilterIds.includes(f.id))
            .map(f => f.id)}
          setValues={clickedValues => {
            toggleMultiSelectDisplayFilter(
              Array.isArray(clickedValues) ? clickedValues : [clickedValues],
            );
          }}
          width={260}
        />
      )}
      {(defaultFilterIds.some(id => !selectedFilterIds.includes(id)) ||
        defaultFilterIds.length !== selectedFilterIds.length) && (
        <Button variant="link" onClick={resetDisplayFilterValues} slim>
          {t("action.reset")}
        </Button>
      )}
    </div>
  );
}

const displayFilterSelectorsContainerCss = css`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacings.xs};
`;
